import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { scrollToSection } from "@/lib/scroll";
import { usePageMeta } from "@/hooks/usePageMeta";

const TIERS = [
  {
    name: "Launch",
    price: "Call for more info",
    blurb: "For businesses ready to look the part and start filling the pipeline.",
    features: ["Website rebuild with new SEO", "Weekly SEO updates", "Google Ads weekly management", "Local listings & citation setup", "Landing page A/B testing", "Monthly performance report"],
    testid: "pricing-tier-launch",
    featured: false,
  },
  {
    name: "Scale",
    price: "Call for more info",
    blurb: "Our most popular engagement — full paid media plus search architecture.",
    features: ["Everything in Launch", "Meta & Yelp Ads management", "Weekly on-site commercial filming & content", "SEO & content engine", "Conversion rate optimization", "Email & SMS retention campaigns", "Custom analytics dashboard", "Bi-weekly partner strategy calls"],
    testid: "pricing-tier-scale",
    featured: true,
  },
  {
    name: "Dominate",
    price: "Call for more info",
    blurb: "The full agency as your growth department, engineered around one goal.",
    features: ["Everything in Scale", "Full brand & web rebuilds", "Dedicated senior pod", "Creative testing at velocity", "Multi-location campaign management", "Executive reporting & board decks", "Quarterly growth planning"],
    testid: "pricing-tier-dominate",
    featured: false,
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  usePageMeta(
    "Packages — Launch, Scale & Dominate | Build Right Partners",
    "Marketing packages for California businesses: website rebuilds with SEO, Google Ads weekly management, Meta & Yelp Ads, weekly on-site filming, and full growth engagements. Call 1-833-995-7227."
  );
  const goContact = () => {
    navigate("/");
    setTimeout(() => scrollToSection("#contact"), 450);
  };

  return (
    <PageShell testid="pricing-page" overline="Engagement Models" title="Invest like you" italic="mean it.">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-28">
        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.name}
              data-testid={t.testid}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className={`relative p-10 flex flex-col border transition-colors duration-500 ${
                t.featured
                  ? "bg-[#0F1A3A] border-amber-400/50 shadow-[0_0_45px_rgba(212,175,55,0.12)]"
                  : "bg-[#0A1128] border-amber-500/15 hover:border-amber-500/35"
              }`}
            >
              {t.featured && (
                <span className="absolute -top-3 left-10 bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] px-4 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-[#060B18] font-semibold">
                  Most Chosen
                </span>
              )}
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400/90">{t.name}</p>
              <a
                href="tel:+18339957227"
                data-testid={`pricing-call-${t.name.toLowerCase()}`}
                className="mt-5 inline-block font-serif text-3xl lg:text-4xl font-semibold italic text-transparent bg-clip-text bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#AA7C11] hover:opacity-80 transition-opacity"
              >
                {t.price}
              </a>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">1-833-995-7227 · toll-free</p>
              <p className="mt-4 text-sm font-light leading-relaxed text-slate-300/85">{t.blurb}</p>
              <span className="gold-hairline my-8" />
              <ul className="space-y-3.5 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                    <Check size={15} className="mt-0.5 shrink-0 text-amber-400" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                onClick={goContact}
                className={`mt-10 w-full rounded-full py-3.5 font-mono text-xs uppercase tracking-[0.25em] font-semibold transition-all duration-300 ${
                  t.featured
                    ? "bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#AA7C11] text-[#060B18] hover:shadow-[0_0_35px_rgba(212,175,55,0.35)]"
                    : "border border-amber-400/40 text-amber-200 hover:bg-amber-400/10"
                }`}
              >
                Start the Conversation
              </button>
            </motion.div>
          ))}
        </div>
        <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-slate-600">
          Every engagement is month-to-month after the first quarter. We earn the renewal.
        </p>
      </section>
    </PageShell>
  );
}
